import { Fragment, useCallback, useMemo, useState } from 'react'

import { Divider, Icon, MenuV2 } from '@circlefin/components'
import { useModal } from '@circlefin/modal-router'
import { usePermission } from '@circlefin/permissions/context'
import { MULTI_ENTITY_MANAGE_ACCOUNTS } from '@services/permissions'
import { homeSection } from '@services/sections/lib/home'
import { l3Onboarding } from '@services/sections/lib/onboarding'
import { manageAccounts } from '@services/sections/lib/settings'
import { routes } from '@services/sections/modal'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'

import type { Section } from '@services/sections/types'
import type { AvailableEntity, LegalEntity } from 'next-auth'

type SelectOption = AvailableEntity | 'manage-accounts'

export interface EntitySwitcherMenuProps {
  /**
   * Class name.
   */
  className?: string
  /**
   * Route to navigate to after switching to a completed entity.
   */
  completedEntityRoute?: Section
}

export const EntitySwitcherMenu: React.FC<EntitySwitcherMenuProps> = ({
  className,
  completedEntityRoute = homeSection,
}) => {
  const { data: session, status } = useSession()
  const { t } = useTranslation('layout')
  const { t: tManageAccounts } = useTranslation('manageAccounts')
  const [, entityManagePermission] = usePermission(MULTI_ENTITY_MANAGE_ACCOUNTS)
  const router = useRouter()
  const modal = useModal()

  const [isSwitchingEntity, setIsSwitchingEntity] = useState(false)

  const availableEntities = useMemo(
    () => session?.availableEntities ?? [],
    [session],
  )

  const selectedUserEntity = useMemo(() => {
    if (!session?.user.entityId) {
      return
    }

    const selected = availableEntities.find(
      (entity) => entity.entityId === session.user.entityId,
    )

    if (!selected) {
      return
    }

    return {
      label: t('nav.entitySwitcher.fallbackEntityId', {
        entityId: selected.entityId.slice(-4),
      }),
      description: selected.institutionName,
      value: selected,
    }
  }, [session?.user.entityId, availableEntities, t])

  const onSelectEntity = useCallback(
    (selected?: SelectOption) => (event: React.MouseEvent) => {
      event.stopPropagation()

      if (selected == null) {
        return
      }

      if (selected === 'manage-accounts') {
        void router.push(manageAccounts.route)
        return
      }

      setIsSwitchingEntity(true)

      modal.router.push({
        pathname: routes.account.entitySwitcher.entitySwitcher,
        query: {
          requestedEntityId: selected.entityId,
          route:
            selected.userState === 'ACTIVATED'
              ? l3Onboarding.route
              : completedEntityRoute.route,
        },
      })
    },
    [modal.router, router, completedEntityRoute.route],
  )

  const options = useMemo(() => {
    const grouped: Map<LegalEntity | 'onboarding', AvailableEntity[]> =
      new Map()

    const onboardingCompleted = availableEntities.filter(
      (entity) => entity.userState === 'APPROVED',
    )

    for (const entity of onboardingCompleted) {
      if (entity.legalEntity) {
        const list = grouped.get(entity.legalEntity) ?? []
        list.push(entity)

        grouped.set(entity.legalEntity, list)
      }
    }

    const onboardingInProgress = availableEntities.filter(
      (entity) => entity.userState === 'ACTIVATED',
    )

    if (onboardingInProgress.length > 0) {
      grouped.set('onboarding', onboardingInProgress)
    }

    const options: React.ReactElement[] = []

    for (const [legalEntity, entities] of grouped.entries()) {
      const headerLabel =
        legalEntity === 'onboarding'
          ? t('nav.entitySwitcher.application')
          : tManageAccounts(`circleLegalEntityType.${legalEntity}`)

      options.push(
        <MenuV2.Item
          key={headerLabel}
          item={{
            label: headerLabel,
            header: true,
          }}
        />,
      )

      entities.forEach((entity) => {
        options.push(
          <MenuV2.Item
            key={entity.entityId}
            disabled={
              entity.entityId === selectedUserEntity?.value.entityId ||
              isSwitchingEntity
            }
            item={{
              label: t('nav.entitySwitcher.fallbackEntityId', {
                entityId: entity.entityId.slice(-4),
              }),
              description: entity.institutionName || '',
              icon:
                legalEntity === 'onboarding'
                  ? 'DocumentTextOutline'
                  : 'OfficeBuildingOutline',
              value: entity,
            }}
            onClick={onSelectEntity(entity)}
          />,
        )
      })
    }

    if (entityManagePermission.isAuthorized) {
      options.push(
        <Fragment key="manage-accounts">
          <div className="px-3 pt-1">
            <Divider />
          </div>
          <MenuV2.Item
            disabled={isSwitchingEntity}
            item={{
              icon: 'CogOutline',
              label: t('nav.entitySwitcher.manageAccounts'),
              value: 'manage-accounts',
            }}
            onClick={onSelectEntity('manage-accounts')}
          />
        </Fragment>,
      )
    }

    return options
  }, [
    availableEntities,
    entityManagePermission.isAuthorized,
    isSwitchingEntity,
    onSelectEntity,
    selectedUserEntity?.value.entityId,
    t,
    tManageAccounts,
  ])

  const handleBlur = useCallback(() => {
    // eslint-disable-next-line sonarjs/todo-tag
    // TODO: fix in components-web, we are overriding the default blur behavior,
    // this is causing the menu to close when the user clicks on an item prior to item handler being called.
  }, [])

  if (status !== 'authenticated' || !selectedUserEntity) {
    return null
  }

  return (
    <MenuV2
      align="left"
      className={classNames(
        'w-full [&_.content]:w-full [&_.square]:border [&_.square]:border-neutral-subtle [&_.select-label]:!font-circular-regular [&_.cb-select-list-item-header.disabled]:!opacity-100',
        className,
      )}
      data-testid="entity-switcher"
      disabled={isSwitchingEntity}
      dropDirection="down"
      role="menu"
    >
      <MenuV2.Trigger
        arrow="show"
        className="w-full [&_.cb-menu-trigger-arrow]:shrink-0 [&_.cb-menu-trigger-content]:grow"
        data-testid="entity-switcher-trigger"
        onBlur={handleBlur}
      >
        <div className="ml-[-3px] flex w-full flex-nowrap items-start gap-x-2">
          <Icon
            className="shrink-0"
            name={
              selectedUserEntity.value.userState === 'ACTIVATED'
                ? 'DocumentTextOutline'
                : 'OfficeBuildingOutline'
            }
            size={20}
          />

          <div className="flex items-center gap-2">
            <div className="flex flex-col items-start gap-y-1">
              <span className="line-clamp-1 break-all text-neutral type-body-sm-bold">
                {selectedUserEntity.label}
              </span>

              <span className="line-clamp-1 break-all text-neutral type-body-sm">
                {selectedUserEntity.description}
              </span>
            </div>
          </div>
        </div>
      </MenuV2.Trigger>

      <MenuV2.Options
        className="w-100"
        selected={{
          label: selectedUserEntity.label,
          description: selectedUserEntity.description,
          value: selectedUserEntity.value,
        }}
      >
        {options}
      </MenuV2.Options>
    </MenuV2>
  )
}
